/**
 * Minimal REST client for the JSON surface (protocol.md §3). Adds the standard headers
 * (Authorization, X-Client-Version, X-Protocol-Version, Accept-Language), maps non-2xx bodies to a
 * {@link ProtocolError}, and supports the Idempotency-Key header for non-idempotent POSTs.
 *
 * `fetch` is injectable for deterministic tests; it defaults to the platform global (Node 22 + all
 * browsers ship it). DOM-free.
 */
import { PROTOCOL_VERSION, ProtocolError, isApiError } from '../protocol/index.ts';

export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

export interface HttpClientOptions {
  baseUrl: string;
  clientVersion: string;
  language: string;
  /** Access token (in-memory only, security §2.1). Read fresh per request; null → no auth header. */
  tokenProvider?: () => string | null;
  fetchImpl?: FetchLike;
}

export interface RequestOptions {
  body?: unknown;
  idempotencyKey?: string;
  /** Attach the bearer token (default true). Auth endpoints pass false. */
  auth?: boolean;
  /** Cookie mode for this request. Auth endpoints pass 'include' so the HttpOnly refresh cookie flows (security §2.1). */
  credentials?: RequestCredentials;
  signal?: AbortSignal;
}

export class HttpClient {
  private readonly o: HttpClientOptions;
  private readonly fetchImpl: FetchLike;

  constructor(options: HttpClientOptions) {
    this.o = options;
    this.fetchImpl = options.fetchImpl ?? ((input, init) => fetch(input, init));
  }

  get<T>(path: string, opts: RequestOptions = {}): Promise<T> {
    return this.request<T>('GET', path, opts);
  }
  post<T>(path: string, opts: RequestOptions = {}): Promise<T> {
    return this.request<T>('POST', path, opts);
  }
  delete<T>(path: string, opts: RequestOptions = {}): Promise<T> {
    return this.request<T>('DELETE', path, opts);
  }

  async request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
    const headers: Record<string, string> = {
      Accept: 'application/json',
      'X-Client-Version': this.o.clientVersion,
      'X-Protocol-Version': String(PROTOCOL_VERSION),
      'Accept-Language': this.o.language,
    };
    if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
    if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
    if (opts.auth !== false) {
      const token = this.o.tokenProvider?.();
      if (token) headers.Authorization = `Bearer ${token}`;
    }

    const init: RequestInit = { method, headers };
    if (opts.body !== undefined) init.body = JSON.stringify(opts.body);
    if (opts.credentials) init.credentials = opts.credentials;
    if (opts.signal) init.signal = opts.signal;

    const response = await this.fetchImpl(`${this.o.baseUrl}${path}`, init);
    return this.parse<T>(response);
  }

  private async parse<T>(response: Response): Promise<T> {
    const text = await response.text();
    const data: unknown = text.length ? safeJson(text) : undefined;
    if (!response.ok) {
      const error = isApiError(data)
        ? data
        : { code: `HTTP_${response.status}`, message: response.statusText || 'Request failed' };
      throw new ProtocolError(error, response.status);
    }
    return data as T;
  }
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}
